export type PnlRange = '1d' | '3d' | '7d' | '30d' | '90d' | 'all'

// ─── /pnl_history/v2 — interval semantics (design-pnl-dashboard.md §6.3) ───

export interface PnlBoundaryCoverage {
  status: 'sampled' | 'fallback' | 'missing'
  anchor_ts: string | null
}

export interface PnlV2Coverage {
  upnl_start: PnlBoundaryCoverage
  upnl_end: PnlBoundaryCoverage
  equity_start: PnlBoundaryCoverage
  equity_end: PnlBoundaryCoverage
}

export interface PnlV2Header {
  period_pnl: number
  realized: number
  unrealized_change: number
  funding: number
  fee: number
  rebate: number
  adjustment: number
  transfer: number
  equity_start: number | null
  equity_end: number | null
  unreconciled_diff: number | null
  since: string
  until: string
  data_quality: 'ok' | 'partial'
  coverage: PnlV2Coverage
  approximate_exchanges: string[]
}

export interface PnlV2Point {
  ts: string
  realized_cum: number
  funding_cum: number
  fee_cum: number
  rebate_cum: number
  adjustment_cum: number
  unrealized_change: number
  period_pnl: number
  current_unrealized: number
}

export interface PnlV2AccountRow {
  owner: string
  period_pnl: number
  realized: number
  unrealized_change: number
  funding: number
  fee: number
  rebate: number
  adjustment: number
  transfer: number
  equity_start: number | null
  equity_end: number | null
  unreconciled_diff: number | null
}

export interface PnlV2Response {
  header: PnlV2Header
  series: PnlV2Point[]
  accounts?: PnlV2AccountRow[]
}

// ─── /pnl_daily (UTC calendar-day attribution) ───

export interface PnlDailyHeader {
  total: number
  realized: number
  funding: number
  fee: number
  unrealized_delta: number
  since: string
  until: string
}

export interface PnlDailyRow {
  day: string
  realized: number
  funding: number
  fee: number
  unrealized_delta: number
  total: number
  partial: boolean
}

export interface PnlDailyResponse {
  header: PnlDailyHeader
  rows: PnlDailyRow[]
}

// ─── /pnl_reconcile_latest ───

export interface PnlReconcileLogRow {
  ts: string
  exchange: string
  owner: string
  account_type: string
  diff: number
  status: 'ok' | 'warn' | 'error'
  note: string | null
}

export interface PnlReconcileLatestResponse {
  rows: PnlReconcileLogRow[]
}

export const RANGE_TO_GRANULARITY: Record<PnlRange, string> = {
  '1d': '5m',
  '3d': '1h',
  '7d': '1h',
  '30d': '1h',
  '90d': '1d',
  all: '1d',
}

export const RANGE_LABELS: Record<PnlRange, string> = {
  '1d': '24h',
  '3d': '3d',
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  all: 'All',
}

export const RANGE_DESCRIPTIONS: Record<PnlRange, string> = {
  '1d': 'Rolling 24 hours',
  '3d': 'Rolling 3 days',
  '7d': 'Rolling 7 days',
  '30d': 'Rolling 30 days',
  '90d': 'Rolling 90 days',
  all: 'Full available history',
}
